/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Box, Button, CircularProgress, type SelectChangeEvent, Typography } from '@mui/material'
import S3Image from 'Components/S3Image'
import { ProgramSelect } from 'Components/Program/ProgramSelect'
import { useRefresh, useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import { useEffect, useState } from 'react'
import 'core/styles.css'
import { Modal } from 'Components/Modal'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { NewEnrollmentStatus, Role, type NewEnrollmentDetails, type ISD, type lookup, type postError } from 'core/types'
import { EnrollmentChip } from 'Components/Enrollment/EnrollmentChip'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { TextFieldWithLabel } from 'Components/TextField'
import { ProgramVerificationChip } from 'Components/Program/ProgramVerificationChip'
import { toast } from 'react-toastify'
import { useAuth } from 'hooks/use-auth'
import { DatePickerWithLabel } from 'Components/DatePicker'

function Layout (props: { show: boolean, onCancel: () => void, onSubmit: () => void, children: JSX.Element, isNew: boolean }): JSX.Element {
  return <Modal
    hideTitle={false}
    title={props.isNew ? 'Add Enrollment' : 'Edit Enrollment'}
    maxWidth={'sm'}
    className={'Nested'}
    sx={{
      position: 'absolute',
      bottom: 0
    }}
    confirmButtonText={props.isNew ? 'Create Enrollment' : 'Update Enrollment'}
    contentStyles={{ p: '0px' }}
    buttonClassName='ButtonDisplay'
    onClose={props.onCancel}
    onConfirm={props.onSubmit}
    open={props.show}
    hideXtoClose={false}
    hideCancelButton={false}
    buttonJustification='Space-Between'
    cancelButtonText='Cancel'
    confirmationContent={props.children}
  />
}

export function EnrollmentEdit (props: {
  isdId?: number | null
  childId?: number | null
  enrollmentId?: number | null
  serviceId?: number | null
  serviceS3Key?: string | null
  show: boolean
  onClose: () => void
  restrictedEdit: boolean
  serviceProviderId?: number | null
}): JSX.Element {
  const auth = useAuth()
  const [data, setData] = useState<NewEnrollmentDetails | null>(null)
  const [initialStatus, setInitialStatus] = useState<number | null>(null)
  const [errorFields, setErrorFields] = useState({ provider: false, schedule: false })
  const [facilities, setFacilities] = useState<Array<{ id: string, name: string }>>([])
  const [exitReasonList, setExitReasonList] = useState<lookup[]>([])
  const [loaded, setLoaded] = useState(false)
  const refreshTime = useRefresh()
  const refreshDispatch = useRefreshDispatch()
  const [submitting, setSubmitting] = useState(false)

  const doRefresh = (): void => {
    refreshDispatch({ type: 'refresh' })
  }
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (props.enrollmentId != null) {
        const { response } = await sendGet(`/NewEnrollment/GetEnrollmentDetails?id=${props.enrollmentId ?? 0}`, {})
        setInitialStatus(response.statusID)
        setData(response)

        const { response: reasonList } = await sendGet('/Enrollment/GetExitReasons', {})
        setExitReasonList(reasonList)
      } else {
        let statusId = NewEnrollmentStatus.Referred
        if (props.isdId != null) {
          const { response: r2 } = await sendGet(`/ISDDetails/GetISDConfig?id=${props.isdId}`, {})
          if ((r2 as ISD).referAllToIntakeProvider ?? false) {
            statusId = NewEnrollmentStatus.PendingRelease
          }
        }
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const newRecord = {
          childID: props.childId,
          isdID: props.isdId,
          serviceID: props.serviceId,
          serviceProviderID: props.serviceProviderId,
          statusID: statusId
        } as NewEnrollmentDetails
        setInitialStatus(statusId)
        setData(newRecord)
      }
      setLoaded(true)
    }
    if (props.show) { void loadData() } else {
      setData(null)
      setLoaded(false)
    }
  }, [props.show, props.isdId, props.enrollmentId, refreshTime])

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, success } = await sendGet(`/NewEnrollment/GetFacilities?providerId=${data?.isdServiceProviderID ?? 0}&programId=${data?.programID ?? 0}`, {})
      if (success) { setFacilities(response) }
    }

    if (data?.isdServiceProviderID != null && data.programID != null) { void loadData() }
  }, [data?.isdServiceProviderID, data?.programID])

  const [bannerOpen, setBannerOpen] = useState(false)
  const closeBanner = (): void => {
    setBannerOpen(false)
  }
  const openBanner = (): void => {
    setBannerOpen(true)
  }
  const scheduleOptions: Array<{ id: string, name: string }> = [
    { id: '0', name: 'Morning' },
    { id: '1', name: 'Afternoon' },
    { id: '2', name: 'Evening' },
    { id: '3', name: 'All Day' }
  ]

  const handleChange = (e: SelectChangeEvent<string>): void => {
    if (data != null) { setData({ ...data, [e.target.name]: e.target.value }) }
  }

  const handleNumberChange = (e: SelectChangeEvent<string>): void => {
    if (data != null) { setData({ ...data, [e.target.name]: e.target.value }) }
  }

  const valueChange = (field: string, e: any): void => {
    if (data !== null) {
      setData({ ...data, [field]: e })
    }
  }

  const programTimeSpanValid = data?.programID != null && data?.timeSpanID != null
  const handleChangeProgram = async (isdSpId: number, serviceProviderId: number, programId: number, programName: string, timeSpanId: number, rank: number | null, serviceProviderName: string): Promise<void> => {
    if (data == null) { return }
    const { response, success } = await sendGet(`/ProgramVerification/FindAvailableVerification?childid=${data.childID}&isdId=${data.isdID}&programId=${programId}&timeSpanId=${timeSpanId}`, {})
    setData({
      ...data,
      statusID: rank != null ? 0 : (initialStatus ?? 1),
      programID: programId,
      timeSpanID: timeSpanId,
      timeSpanName: success ? response.timeSpanName : data.timeSpanName,
      programName,
      serviceProviderID: serviceProviderId,
      serviceProviderName,
      isdServiceProviderID: isdSpId,
      desiredScheduleID: null,
      programVerificationStatus: success ? response.status : 'NotVerified',
      notes: '',
      rank: response.currentMaxRank != null ? (response.currentMaxRank as number) + 1 : null,
      facilityID: null
    })
    closeBanner()
  }
  const onSubmit = async (): Promise<void> => {
    if (!submitting) {
      setSubmitting(true)
      const errors = []
      const errorfields: { provider: boolean, schedule: boolean } = { provider: false, schedule: false }
      if (data?.statusID === NewEnrollmentStatus.Exited) {
        if (data?.exitDate == null) {
          errorfields.schedule = true
          errors.push('Please select an exit date.')
        }
      } else {
        if (data?.desiredScheduleID == null) {
          errorfields.schedule = true
          errors.push('Please select a desired schedule.')
        }
        if (data?.isdServiceProviderID == null) {
          errorfields.provider = true
          errors.push('Please select a service provider.')
        }
      }
      setErrorFields(errorfields)
      if (errors.length > 0) {
        errors.forEach(e => toast.error(e))
        setSubmitting(false)
        return
      }
      if (data == null) {
        setSubmitting(false)
        return
      }
      if (data?.statusID === NewEnrollmentStatus.Exited) {
        const result = await sendPost('/NewEnrollment/UpdateExitInfo', { ...data })
        const rsp: { success: boolean, errors: postError[], newRecordID: number | null } = result.response
        if (rsp.success) {
          toast.success('Exit information was updated successfully.')
          doRefresh()
          props.onClose()
        } else {
          rsp.errors.forEach((error) => {
            toast.error(error.error)
          })
        }
      } else {
        const rsp = await sendPost('/NewEnrollment/UpdateEnrollment', { ...data })
        if (rsp.success) {
          if (data.statusID === NewEnrollmentStatus.PendingRelease) { toast.warning('This enrollment was created in pending release status, and will need to be released before it can be acted on.') }
          toast.success(rsp.response.message)
          doRefresh()
          props.onClose()
        }
      }
      setSubmitting(false)
    }
  }
  if (!loaded) return <></>
  if (data == null) { return <CircularProgress /> }
  return <Layout show={props.show} onCancel={props.onClose} onSubmit={onSubmit} isNew={props.enrollmentId == null}>
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'left' }}>
      <Box>{data.serviceProviderName}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, pb: '10px' }}>
        <S3Image imageKey={(props.enrollmentId != null ? data?.serviceS3Key : props.serviceS3Key) ?? ''} style={{ height: '20px', width: '20px' }} />
        <Typography>{data?.serviceName}</Typography>
        <Box sx={{ width: 'fit-content' }}>
          {programTimeSpanValid && (
            <ProgramVerificationChip programName={data.programName} status={data.programVerificationStatus} timeSpanName={data.timeSpanName ?? ''} showStatusName={true} />
          )}
        </Box>
      </Box>
      {!props.restrictedEdit && (<Box sx={{ py: '10px', alignSelf: 'center' }}>
        <Button variant='text' sx={{ width: 'fit-content', textTransform: 'none', color: 'inherit', textDecoration: 'underline', fontWeight: 'bold', alignSelf: 'center' }} onClick={openBanner}>Select Program / School Year</Button>
      </Box>
      )}
      {data.statusID === NewEnrollmentStatus.Exited
        ? <>
          <DatePickerWithLabel
            label='Exit Date'
            name='exitDate'
            dataTestId='exitDate'
            required={true}
            onChange={(date: Date | null) => { valueChange('exitDate', date ?? null) }}
            value={data?.exitDate}
          />
          <SelectWithLabel
            label='Exit Reason'
            name='exitReasonID'
            width='100%'
            required={true}
            onChange={(evt: any) => { valueChange('exitReasonID', evt.target.value) }}
            options={exitReasonList}
            value={data.exitReasonID ?? ''}
          /></>
        : <>
          <ProgramSelect
            enrollmentId={data.id}
            show={bannerOpen}
            childId={data.childID}
            isdid={data.isdID}
            providerId={data.serviceProviderID}
            programId={data.programID}
            serviceId={data.serviceID}
            timeSpanId={data.timeSpanID}
            cancel={closeBanner} submitted={handleChangeProgram} variant={'banner'}
            restrictProvider={![Role.ISD_USER, Role.INTAKE_USER, Role.MIECC_ADMIN].includes(auth.user?.securityLevel ?? Role.SERVICE_PROVIDER_USER)}
          />
          {programTimeSpanValid && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, pt: '10px' }}>
                <Box>New Enrollment Status</Box>
                <EnrollmentChip statusId={data.statusID} />
                {data.statusID === NewEnrollmentStatus.PendingRelease && (
                  <TextFieldWithLabel name={'rank'} type='number' disabled label='Current Rank' onChange={handleNumberChange} value={data.rank ?? 1} />
                )}
              </Box>
              <SelectWithLabel
                width='100%'
                label='Facility'
                name='facilityID'
                onChange={handleChange}
                value={data.facilityID ?? ''}
                options={facilities}
              />
              <SelectWithLabel
                width='100%'
                label='Desired Schedule'
                name='desiredScheduleID'
                onChange={handleChange}
                value={data.desiredScheduleID?.toString() ?? ''}
                options={scheduleOptions}
                required={true}
                error={errorFields.schedule}
              />
              <Box width={'95%'}>
                <TextFieldWithLabel
                  label={'Detail'}
                  name={'notes'}
                  onChange={handleChange}
                  value={data.notes ?? ''}
                />
              </Box>
            </>
          )}
        </>}
    </Box>
  </Layout>
}
